/**
 * Utility functions for device tree operations
 * 
 * AI Agent Note: This file contains helper functions for tree traversal,
 * circular dependency detection, and parent-child relationship management.
 */

import { Device } from '../types';

/**
 * Build a map from device ID to parent ID for efficient tree traversal
 * @param devices - Array of all devices
 * @returns Map of device ID to parent ID (or null for root devices)
 */
export function buildParentMap(devices: Device[]): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const device of devices) {
    map.set(device.id, device.connectedTo);
  }
  return map;
}

/**
 * Check if connecting a device to a potential parent would create a circular reference
 * 
 * Example: Device A → Device B → Device C → Device A (circular!)
 * 
 * @param potentialParentId - ID of the device to connect to
 * @param targetDeviceId - ID of the device being connected
 * @param parentMap - Pre-built parent map for efficiency
 * @returns true if circular dependency detected, false otherwise
 */
export function checkCircularRef(
  potentialParentId: string | null,
  targetDeviceId: string,
  parentMap: Map<string, string | null>
): boolean {
  if (!potentialParentId) return false;
  
  const visited = new Set<string>();
  let currentId: string | null = potentialParentId;
  
  while (currentId) {
    if (currentId === targetDeviceId) return true;
    if (visited.has(currentId)) return false;
    
    visited.add(currentId);
    currentId = parentMap.get(currentId) ?? null;
  }
  
  return false;
}

/**
 * Get all children devices connected to a parent
 * Includes both power connections and USB connections
 * Uses the most specific connection type available
 * @param parentId - ID of the parent device
 * @param devices - Array of all devices
 * @returns Array of child devices
 */
export function getConnectedDevices(parentId: string, devices: Device[]): Device[] {
  return devices.filter(d => {
    // Check explicit power connection first
    if (d.powerConnection?.sourceDeviceId) {
      return d.powerConnection.sourceDeviceId === parentId;
    }
    // Check USB connection if no power connection
    if (d.usbConnection?.sourceDeviceId && !d.powerConnection) {
      return d.usbConnection.sourceDeviceId === parentId;
    }
    // Fall back to legacy connectedTo
    return d.connectedTo === parentId;
  });
}

/**
 * Get all root devices (devices with no parent connection)
 * Root devices are either power sources or devices with no power/USB connections
 * @param devices - Array of all devices
 * @returns Array of root devices
 */
export function getRootDevices(devices: Device[]): Device[] {
  return devices.filter(d => 
    !d.connectedTo && 
    !d.powerConnection && 
    !d.usbConnection
  );
}

/**
 * Find a device by ID
 * @param deviceId - ID of the device to find
 * @param devices - Array of all devices
 * @returns Device if found, undefined otherwise
 */
export function findDeviceById(deviceId: string, devices: Device[]): Device | undefined {
  return devices.find(d => d.id === deviceId);
}

/**
 * Get available parent devices for a given device (excludes self and prevents circular refs)
 * Only returns devices that can provide power (have powerOutputs)
 * @param currentDevice - Device being edited (or undefined for new device)
 * @param devices - Array of all devices
 * @returns Array of devices that can be selected as parent
 */
export function getAvailableParentDevices(
  currentDevice: Device | undefined,
  devices: Device[]
): Device[] {
  if (!currentDevice) {
    // For new devices, only show devices with power outputs
    return devices.filter(d => d.powerOutputs && d.powerOutputs.length > 0);
  }
  
  const parentMap = buildParentMap(devices);
  
  return devices.filter(d => {
    // Exclude self
    if (d.id === currentDevice.id) return false;
    
    // Only include devices that can provide power
    if (!d.powerOutputs || d.powerOutputs.length === 0) return false;
    
    // Check for circular reference
    if (checkCircularRef(d.id, currentDevice.id, parentMap)) return false;
    
    return true;
  });
}
